import { Card } from '@/components/ui/Card';
import { SnapshotChangePill } from '@/components/snapshot/SnapshotChangePill';
import {
  formatSnapshotCurrency,
  formatSnapshotCurrencyPrecise,
  formatSnapshotNumber,
  formatSnapshotPercent,
  type SnapshotTotals,
} from '@/api/snapshotMetrics';

type KpiDef = {
  key: keyof SnapshotTotals | 'cpm' | 'rpc' | 'cpo' | 'cpa';
  label: string;
  format: (v: number) => string;
  polarity: 'higher_is_better' | 'lower_is_better';
  getValue: (totals: SnapshotTotals, derived: { cpm: number; rpc: number; cpo: number }) => number;
};

const KPI_DEFS: KpiDef[] = [
  {
    key: 'cpc',
    label: 'CPC',
    format: formatSnapshotCurrencyPrecise,
    polarity: 'lower_is_better',
    getValue: (t) => t.cpc,
  },
  {
    key: 'cpm',
    label: 'CPM',
    format: formatSnapshotCurrencyPrecise,
    polarity: 'lower_is_better',
    getValue: (_, d) => d.cpm,
  },
  {
    key: 'ctr',
    label: 'CTR%',
    format: formatSnapshotPercent,
    polarity: 'higher_is_better',
    getValue: (t) => t.ctr,
  },
  {
    key: 'impressions',
    label: 'Impressions',
    format: (v) => formatSnapshotNumber(v, 0),
    polarity: 'higher_is_better',
    getValue: (t) => t.impressions,
  },
  {
    key: 'clicks',
    label: 'Clicks',
    format: (v) => formatSnapshotNumber(v, 0),
    polarity: 'higher_is_better',
    getValue: (t) => t.clicks,
  },
  {
    key: 'rpc',
    label: 'RPC',
    format: formatSnapshotCurrency,
    polarity: 'higher_is_better',
    getValue: (_, d) => d.rpc,
  },
  {
    key: 'cpo',
    label: 'CPO',
    format: formatSnapshotCurrencyPrecise,
    polarity: 'lower_is_better',
    getValue: (_, d) => d.cpo,
  },
  {
    key: 'cpa',
    label: 'CPA',
    format: formatSnapshotCurrencyPrecise,
    polarity: 'lower_is_better',
    getValue: (_, d) => d.cpo,
  },
];

const KPI_ACCENTS = [
  'border-l-rose-400',
  'border-l-violet-400',
  'border-l-sky-400',
  'border-l-amber-400',
  'border-l-emerald-400',
  'border-l-pink-400',
  'border-l-brand',
  'border-l-teal-400',
];

function KpiTile({
  def,
  current,
  previous,
  derived,
  prevDerived,
  compareEnabled,
  accentClass,
}: {
  def: KpiDef;
  current: SnapshotTotals;
  previous: SnapshotTotals;
  derived: { cpm: number; rpc: number; cpo: number };
  prevDerived: { cpm: number; rpc: number; cpo: number };
  compareEnabled: boolean;
  accentClass: string;
}) {
  const cur = def.getValue(current, derived);
  const prev = def.getValue(previous, prevDerived);

  return (
    <Card className={`border-l-4 p-4 ${accentClass}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-foreground-muted">{def.label}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <p className="text-xl font-bold text-foreground">{def.format(cur)}</p>
        <SnapshotChangePill
          current={cur}
          previous={prev}
          polarity={def.polarity}
          show={compareEnabled}
        />
      </div>
      {compareEnabled ? (
        <p className="mt-1 text-xs text-foreground-muted">Prev: {def.format(prev)}</p>
      ) : null}
    </Card>
  );
}

export function SnapshotKpiGrid({
  currentTotals,
  previousTotals,
  currentDerived,
  previousDerived,
  compareEnabled,
}: {
  currentTotals: SnapshotTotals;
  previousTotals: SnapshotTotals;
  currentDerived: { cpm: number; rpc: number; cpo: number };
  previousDerived: { cpm: number; rpc: number; cpo: number };
  compareEnabled: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {KPI_DEFS.map((def, idx) => (
        <KpiTile
          key={`${def.key}-${idx}`}
          def={def}
          current={currentTotals}
          previous={previousTotals}
          derived={currentDerived}
          prevDerived={previousDerived}
          compareEnabled={compareEnabled}
          accentClass={KPI_ACCENTS[idx % KPI_ACCENTS.length]}
        />
      ))}
    </div>
  );
}
